/**
 * EdgeLayer - Renders all flow edges as SVG paths
 *
 * Displays curved paths between nodes with colors based on edge type.
 * Supports hover effects, click handlers, and tooltip display.
 */

import { memo, useState, useCallback } from 'react';
import type { FlowEdge, PositionedEdge } from './types';
import { getEdgeColor, getEdgeHoverColor } from './layout';
import { A11Y } from './constants';

export interface EdgeLayerProps {
  positionedEdges: PositionedEdge[];
  onEdgeClick?: (edge: FlowEdge) => void;
  /** Called when mouse enters an edge */
  onEdgeHover?: (edge: FlowEdge | null, event?: React.MouseEvent) => void;
  /** ID of the currently highlighted edge (if any) */
  highlightedEdgeId?: string | null;
}

interface EdgePathProps {
  positionedEdge: PositionedEdge;
  onClick?: (edge: FlowEdge) => void;
  onMouseEnter?: (edge: FlowEdge, event: React.MouseEvent) => void;
  onMouseLeave?: (edge: FlowEdge) => void;
  isHighlighted?: boolean;
}

/**
 * Individual edge path component with hover state
 */
const EdgePath = memo(function EdgePath({
  positionedEdge,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isHighlighted = false,
}: EdgePathProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const { edge, path, strokeWidth } = positionedEdge;

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      setIsHovered(true);
      onMouseEnter?.(edge, e);
    },
    [edge, onMouseEnter]
  );

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onMouseLeave?.(edge);
  }, [edge, onMouseLeave]);

  const handleClick = useCallback(() => {
    onClick?.(edge);
  }, [onClick, edge]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(edge);
      }
    },
    [onClick, edge]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Determine visual state
  const showHighlight = isHovered || isFocused || isHighlighted;

  // Determine colors
  const strokeColor = showHighlight
    ? getEdgeHoverColor(edge.type)
    : getEdgeColor(edge.type);

  // Determine if this is a transfer edge (dashed line)
  const isTransfer = edge.type === 'transfer';

  return (
    <g className="edge-path-group">
      {/* Focus/highlight outline (rendered behind the main path) */}
      {(isFocused || isHighlighted) && (
        <path
          d={path}
          stroke={A11Y.FOCUS_RING.split(' ')[2]}
          strokeWidth={strokeWidth + 4}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={isTransfer ? '8,4' : undefined}
          opacity={0.3}
          className="pointer-events-none"
        />
      )}

      {/* Main edge path */}
      <path
        d={path}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={isTransfer ? '8,4' : undefined}
        className="sankey-edge transition-all duration-150"
        role="button"
        tabIndex={0}
        aria-label={`Flow from ${edge.source} to ${edge.target}: $${edge.value}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      />
    </g>
  );
});

function EdgeLayerComponent({
  positionedEdges,
  onEdgeClick,
  onEdgeHover,
  highlightedEdgeId,
}: EdgeLayerProps) {
  const handleEdgeMouseEnter = useCallback(
    (edge: FlowEdge, event: React.MouseEvent) => {
      onEdgeHover?.(edge, event);
    },
    [onEdgeHover]
  );

  const handleEdgeMouseLeave = useCallback(() => {
    onEdgeHover?.(null);
  }, [onEdgeHover]);

  if (positionedEdges.length === 0) {
    return null;
  }

  // Separate income, expense, and transfer edges for layering
  const incomeEdges = positionedEdges.filter((pe) => pe.edge.type === 'income');
  const expenseEdges = positionedEdges.filter((pe) => pe.edge.type === 'expense');
  const transferEdges = positionedEdges.filter((pe) => pe.edge.type === 'transfer');

  return (
    <g className="edge-layer">
      {/* Define gradients for edges */}
      <defs>
        {/* Income gradient (green) */}
        <linearGradient id="income-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(46, 125, 50, 0.35)" />
          <stop offset="100%" stopColor="rgba(46, 125, 50, 0.25)" />
        </linearGradient>

        {/* Expense gradient (red) */}
        <linearGradient id="expense-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(198, 40, 40, 0.25)" />
          <stop offset="100%" stopColor="rgba(198, 40, 40, 0.35)" />
        </linearGradient>

        {/* Transfer gradient (orange) */}
        <linearGradient id="transfer-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(245, 124, 0, 0.35)" />
          <stop offset="100%" stopColor="rgba(245, 124, 0, 0.35)" />
        </linearGradient>
      </defs>

      {/* Render edges in layers (income first, then expense, transfers on top) */}
      <g className="income-edges">
        {incomeEdges.map((pe) => (
          <EdgePath
            key={pe.edge.id}
            positionedEdge={pe}
            onClick={onEdgeClick}
            onMouseEnter={handleEdgeMouseEnter}
            onMouseLeave={handleEdgeMouseLeave}
            isHighlighted={highlightedEdgeId === pe.edge.id}
          />
        ))}
      </g>

      <g className="expense-edges">
        {expenseEdges.map((pe) => (
          <EdgePath
            key={pe.edge.id}
            positionedEdge={pe}
            onClick={onEdgeClick}
            onMouseEnter={handleEdgeMouseEnter}
            onMouseLeave={handleEdgeMouseLeave}
            isHighlighted={highlightedEdgeId === pe.edge.id}
          />
        ))}
      </g>

      <g className="transfer-edges">
        {transferEdges.map((pe) => (
          <EdgePath
            key={pe.edge.id}
            positionedEdge={pe}
            onClick={onEdgeClick}
            onMouseEnter={handleEdgeMouseEnter}
            onMouseLeave={handleEdgeMouseLeave}
            isHighlighted={highlightedEdgeId === pe.edge.id}
          />
        ))}
      </g>
    </g>
  );
}

export const EdgeLayer = memo(EdgeLayerComponent);
export default EdgeLayer;
